import React, { useState, useEffect, useRef } from 'react';
import './index.scss';
import {
  Button,
  Form,
  DatePicker,
  InputNumber,
  Input,
  Select,
  Checkbox,
  Spin,
  message,
  theme as antdTheme,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useTheme } from '@/contexts/ThemeContext';
import {
  retrieveQuote,
  createQuote,
  updateQuote,
  createApplication,
  retrieveApplication,
  retrievePolicies,
} from '@/services/insurance-service';
import {
  GenderOption,
  TobaccoUse,
  type QuoterBody,
  type QuoterResponse,
  type ApplicationResponse,
  type PolicyInfo,
} from '@/types/insurance';

type Step = 'loading' | 'intro' | 'quote-info' | 'results' | 'no-offer' | 'portal';

const DEFAULT_COVERAGE = 1_000_000;

// Coverage amounts Ladder will quote, mirroring the mobile app's list
// (FOFrontEnd/insurance/insurance-quote-display.tsx).
const AVAILABLE_COVERAGE = [
  100000, 150000, 200000, 250000, 300000, 350000, 400000, 450000, 500000, 550000, 600000, 650000,
  700000, 750000, 800000, 850000, 900000, 950000, 1000000, 1100000, 1200000, 1300000, 1400000,
  1500000, 1600000, 1700000, 1800000, 1900000, 2000000, 2250000, 2500000, 2750000, 3000000, 3250000,
  3500000, 3750000, 4000000, 4250000, 4500000, 4750000, 5000000, 5500000, 6000000, 6500000, 7000000,
  7500000, 8000000,
];

// Where we send users to pick up an in-progress application or review issued
// policies — Ladder owns both once the hand-off has happened.
const LADDER_LOGIN_URL = 'https://www.ladderlife.com/login';

const IMPORTANT_NOTICE =
  'All prices are in USD and based on your current inputs. While we do our best to be accurate, these prices may differ from your final offer.';

// Friendly labels for the raw application status codes the backend returns.
const APPLICATION_STATUS_LABELS: Record<string, string> = {
  application_incomplete: 'Incomplete',
  application_incomplete_fallback_shown: 'Incomplete',
  application_not_started: 'Not started',
  application_complete_action_needed: 'Action needed',
  application_complete_pending_underwriting: 'Pending underwriting',
  offer_pending: 'Offer pending',
  covered: 'Covered',
  application_closed: 'Closed',
  application_declined: 'Declined',
};

// Ladder reserves the right to add statuses (dev has already returned a
// literal "…new_statuses_may_be_added_later" sentinel). Only say "No
// application" when there genuinely isn't one — an application with a status
// we don't recognise is still an application in progress.
const applicationStatusLabel = (status?: string): string => {
  if (!status) return 'No application';
  return APPLICATION_STATUS_LABELS[status] ?? 'In progress';
};

const genderOptions: { label: string; value: GenderOption }[] = [
  { label: 'Male', value: GenderOption.Male },
  { label: 'Female', value: GenderOption.Female },
  { label: 'Other', value: GenderOption.Other },
];

const tobaccoOptions: { label: string; value: TobaccoUse }[] = [
  { label: 'Never', value: TobaccoUse.Never },
  { label: 'Within the last 12 months', value: TobaccoUse.WithinLast12Months },
  { label: '12–23 months ago', value: TobaccoUse.TwelveToTwentyThreeMonths },
  {
    label: '24–35 months ago',
    value: TobaccoUse.TwentyFourToThirtyFiveMonths,
  },
  { label: '36+ months ago', value: TobaccoUse.ThirtySixMonthsOrGreater },
];

interface QuoteFormValues {
  sex: GenderOption;
  date_of_birth: Dayjs;
  feet: number;
  inches: number;
  weight_pounds: number;
  household_income: number;
  zipcode: string;
  tobacco_use: TobaccoUse;
  family_history?: boolean;
}

// antd Radio.Button renders white against our themed surfaces, so this is a
// custom control. Shaped as value/onChange so Form.Item drives it like any
// other field (validation + error rendering come for free).
const GenderSelect: React.FC<{
  value?: GenderOption;
  onChange?: (value: GenderOption) => void;
}> = ({ value, onChange }) => (
  <div className="gender-options">
    {genderOptions.map((o) => (
      <button
        type="button"
        key={o.value}
        className={`gender-option${value === o.value ? ' selected' : ''}`}
        onClick={() => onChange?.(o.value)}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);

// Amplify surfaces HTTP errors with a status code on error.response.statusCode.
const statusCodeOf = (err: unknown): number | undefined =>
  (err as { response?: { statusCode?: number } } | null)?.response?.statusCode;

const Insurance: React.FC = () => {
  const { theme } = useTheme();
  // Drive custom accents from the firm's brand color (antd colorPrimary token),
  // exposed to SCSS as --brand so the pillar respects white-label theming.
  const { token } = antdTheme.useToken();
  const [form] = Form.useForm<QuoteFormValues>();

  const [step, setStep] = useState<Step>('loading');
  const [quoteBody, setQuoteBody] = useState<QuoterBody | null>(null);
  const [quote, setQuote] = useState<QuoterResponse | null>(null);
  const [application, setApplication] = useState<ApplicationResponse | null>(null);
  const [policies, setPolicies] = useState<PolicyInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [requoting, setRequoting] = useState(false);
  // Set only when the browser blocked both attempts to open the Ladder tab, so
  // the user still has a way to reach the application we just created.
  const [blockedApplicationUrl, setBlockedApplicationUrl] = useState<string | null>(null);

  const containerClass = `insurance-container ${theme}-theme`;

  // Steps differ a lot in height, and the surrounding layout scroll container
  // keeps its offset across a step change — so arriving from the tall quote
  // form left the next step scrolled past its own heading. Reset to the top of
  // the pillar whenever the step changes.
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    containerRef.current?.scrollIntoView({ block: 'start' });
  }, [step]);

  // On mount, check for an existing application/policies to decide whether to
  // land on the portal or the intro. allSettled so a 404 (or a CORS/network
  // failure) on either call just falls back to the intro — the page still works.
  useEffect(() => {
    let cancelled = false;
    const loadStatus = async () => {
      const [appRes, polRes] = await Promise.allSettled([
        retrieveApplication(),
        retrievePolicies(),
      ]);
      if (cancelled) return;
      const app = appRes.status === 'fulfilled' ? appRes.value : null;
      const pols = polRes.status === 'fulfilled' ? polRes.value.active_policies : [];
      setApplication(app);
      setPolicies(pols);
      setStep(pols.length > 0 || app?.status ? 'portal' : 'intro');
    };
    void loadStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  // retrieve → (404 ? create : update) → eligibility gate, matching mobile.
  const handleQuoteSubmit = async (values: QuoteFormValues) => {
    const body: QuoterBody = {
      sex: values.sex,
      date_of_birth: values.date_of_birth.format('YYYY-MM-DD'),
      height_inches: values.feet * 12 + values.inches,
      weight_pounds: values.weight_pounds,
      household_income: values.household_income,
      zipcode: values.zipcode,
      tobacco_use: values.tobacco_use,
      family_history: values.family_history ?? false,
      coverage: DEFAULT_COVERAGE,
    };

    const applyResult = (result: QuoterResponse) => {
      setQuoteBody(body);
      setQuote(result);
      setStep(result.quote_status.eligible ? 'results' : 'no-offer');
    };

    // Only Ladder's eligibility gate may send the user to 'no-offer'. A request
    // that simply failed (expired session -> 403, 5xx, network) must not tell
    // someone they can't be insured — surface it as an error and keep the form
    // so they can retry.
    const reportFailure = (context: string, err: unknown) => {
      console.error(context, err);
      message.error("We couldn't get your quote right now. Please try again.");
    };

    setLoading(true);
    try {
      // Does the user already have a quote? (404 = no, so create one.)
      await retrieveQuote();
      applyResult(await updateQuote(body));
    } catch (err) {
      if (statusCodeOf(err) === 404) {
        try {
          applyResult(await createQuote(body));
        } catch (createErr) {
          reportFailure('Failed to create insurance quote:', createErr);
        }
      } else {
        reportFailure('Failed to retrieve/update insurance quote:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  // Re-price at a different coverage amount, mirroring mobile's coverage
  // dropdown. The term cards themselves aren't selectable on either platform —
  // the term is chosen inside Ladder's hosted application.
  const handleCoverageChange = async (coverage: number) => {
    if (!quoteBody) return;
    const body = { ...quoteBody, coverage };
    setRequoting(true);
    try {
      const res = await updateQuote(body);
      setQuoteBody(body);
      setQuote(res);
    } catch (err) {
      console.error('Failed to re-quote at new coverage:', err);
      message.error("We couldn't update your quote. Please try again.");
    } finally {
      setRequoting(false);
    }
  };

  const handleApply = async () => {
    if (!quoteBody) return;
    setApplying(true);
    setBlockedApplicationUrl(null);
    // Open the tab synchronously (within the click) so it isn't popup-blocked,
    // then redirect it to the Ladder-hosted application URL.
    const appWindow = window.open('about:blank', '_blank');
    try {
      const res = await createApplication(quoteBody);
      if (appWindow) {
        appWindow.opener = null;
        appWindow.location.href = res.url;
        message.success('Continue your application in the new tab.');
        return;
      }
      // The synchronous open was blocked. This retry is no longer inside the
      // click gesture, so it is likely blocked too — check before claiming a
      // tab opened. The application already exists on Ladder at this point,
      // so if we can't open it we must surface the link rather than strand
      // the user with an application they can't reach.
      const fallbackWindow = window.open(res.url, '_blank', 'noopener,noreferrer');
      if (fallbackWindow) {
        message.success('Continue your application in the new tab.');
      } else {
        setBlockedApplicationUrl(res.url);
        message.warning('Your browser blocked the new tab — use the link below to continue.');
      }
    } catch (err) {
      console.error('Failed to start insurance application:', err);
      appWindow?.close();
      message.error("We couldn't start your application. Please try again.");
    } finally {
      setApplying(false);
    }
  };

  const renderIntro = () => (
    <div className="insurance-content-box">
      <div className="content-primary">
        <SafetyIcon />
        <h2 className="title">Insurance</h2>
        <p className="description">
          Protect the people who depend on you with term life coverage — instant quotes, tailored
          coverage, and an easy application.
        </p>
        <div className="feature-list">
          {['Instant Quotes', 'Tailored Coverage', 'Apply with Ease', '24/7 Support'].map((f) => (
            <span className="feature-chip" key={f}>
              {f}
            </span>
          ))}
        </div>
      </div>

      <div className="content-secondary">
        <p className="powered-by">Powered by Ladder</p>
        <Button type="primary" className="primary-cta" onClick={() => setStep('quote-info')}>
          <span className="btn-text">Get started</span>
          <ArrowRightOutlined className="arrow-icon" />
        </Button>
      </div>
    </div>
  );

  const renderQuoteInfo = () => (
    <div className="step-box">
      <StepHeader label="Your details" onBack={() => setStep('intro')} />
      <h2 className="step-title">Tell us about yourself</h2>
      <p className="step-subtitle">A few details let Ladder calculate your term life quote.</p>

      <Form<QuoteFormValues>
        form={form}
        layout="vertical"
        className="quote-form"
        requiredMark={false}
        initialValues={{ family_history: false }}
        onFinish={handleQuoteSubmit}
      >
        <Form.Item
          label="What is your sex?"
          name="sex"
          extra="Insurers use this to estimate life expectancy. Select the option on your legal documents."
          rules={[{ required: true, message: 'Please select an option' }]}
        >
          <GenderSelect />
        </Form.Item>

        <div className="form-row">
          <Form.Item
            label="Date of birth"
            name="date_of_birth"
            rules={[{ required: true, message: 'Please enter your date of birth' }]}
          >
            <DatePicker
              className="full-width"
              format="YYYY-MM-DD"
              disabledDate={(d) => !d || d.isAfter(dayjs()) || d.isBefore(dayjs('1900-01-01'))}
            />
          </Form.Item>

          <Form.Item
            label="Zip code"
            name="zipcode"
            rules={[
              { required: true, message: 'Please enter your zip code' },
              { pattern: /^\d{5}$/, message: 'Enter a 5-digit zip code' },
            ]}
          >
            <Input maxLength={5} inputMode="numeric" placeholder="12345" />
          </Form.Item>
        </div>

        <div className="form-row">
          <Form.Item label="Height" required className="height-item">
            <div className="height-row">
              <Form.Item
                name="feet"
                noStyle
                rules={[
                  { required: true, message: 'Feet' },
                  {
                    type: 'number',
                    min: 0,
                    max: 8,
                    message: '0–8',
                  },
                ]}
              >
                <InputNumber placeholder="ft" min={0} max={8} addonAfter="ft" />
              </Form.Item>
              <Form.Item
                name="inches"
                noStyle
                rules={[
                  { required: true, message: 'Inches' },
                  { type: 'number', min: 0, max: 11, message: '0–11' },
                ]}
              >
                <InputNumber placeholder="in" min={0} max={11} addonAfter="in" />
              </Form.Item>
            </div>
          </Form.Item>

          <Form.Item
            label="Weight (lbs)"
            name="weight_pounds"
            rules={[
              { required: true, message: 'Please enter your weight' },
              { type: 'number', min: 1, max: 500, message: 'Enter a valid weight' },
            ]}
          >
            <InputNumber className="full-width" min={1} max={500} placeholder="lbs" />
          </Form.Item>
        </div>

        <div className="form-row">
          <Form.Item
            label="Annual household income"
            name="household_income"
            rules={[
              { required: true, message: 'Please enter your household income' },
              { type: 'number', min: 1, message: 'Enter a valid amount' },
            ]}
          >
            <InputNumber<number>
              className="full-width"
              min={0}
              prefix="$"
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(v) => Number((v ?? '').replace(/[^\d]/g, ''))}
              placeholder="0"
            />
          </Form.Item>

          <Form.Item
            label="Last time you used tobacco"
            name="tobacco_use"
            rules={[{ required: true, message: 'Please select an option' }]}
          >
            <Select options={tobaccoOptions} placeholder="Select an option" />
          </Form.Item>
        </div>

        <Form.Item name="family_history" valuePropName="checked" noStyle>
          <Checkbox className="family-history">
            An immediate family member was diagnosed with a serious illness before age 60
          </Checkbox>
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          className="primary-cta submit-cta"
          loading={loading}
        >
          <span className="btn-text">Get my quote</span>
        </Button>
      </Form>
    </div>
  );

  const renderResults = () => {
    const quotes = quote?.quotes ?? [];
    const coverage = quotes[0]?.coverage ?? DEFAULT_COVERAGE;
    return (
      <div className="step-box">
        <StepHeader label="Your quote" onBack={() => setStep('quote-info')} />
        <h2 className="step-title">You&apos;re covered for {formatCurrency(coverage)}</h2>
        <p className="step-subtitle">
          Adjust your coverage to see how the monthly premium changes, then finish your application
          on Ladder.
        </p>

        <div className="coverage-picker">
          <label className="coverage-label" htmlFor="insurance-coverage">
            Coverage amount
          </label>
          <Select
            id="insurance-coverage"
            className="coverage-select"
            value={coverage}
            loading={requoting}
            disabled={requoting}
            onChange={handleCoverageChange}
            options={AVAILABLE_COVERAGE.map((c) => ({
              label: formatCurrency(c),
              value: c,
            }))}
          />
          <span className="coverage-hint">Default is recommended</span>
        </div>

        <Spin spinning={requoting}>
          <div className="quote-cards">
            {quotes.map((q) => (
              <div className="quote-card" key={`${q.term}-${q.coverage}`}>
                <span className="quote-term">{q.term}-Year Term</span>
                <span className="quote-premium">
                  {formatCurrency(q.monthly_premium)}
                  <span className="per">/mo</span>
                </span>
                <span className="quote-coverage">{formatCurrency(q.coverage)} coverage</span>
              </div>
            ))}
          </div>
        </Spin>

        <p className="disclaimer">{IMPORTANT_NOTICE}</p>
        <p className="disclaimer">
          By proceeding, you agree to share your application details with Wavvest.
        </p>
        <Button type="primary" className="primary-cta" loading={applying} onClick={handleApply}>
          <span className="btn-text">Complete my application on Ladder</span>
          <ArrowRightOutlined className="arrow-icon" />
        </Button>

        {blockedApplicationUrl && (
          <p className="blocked-popup-note">
            Your browser blocked the new tab.{' '}
            <a href={blockedApplicationUrl} target="_blank" rel="noopener noreferrer">
              Open your application on Ladder
            </a>
            .
          </p>
        )}
      </div>
    );
  };

  const renderNoOffer = () => (
    <div className="insurance-content-box">
      <div className="content-primary">
        <SafetyIcon muted />
        <h2 className="title">We can&apos;t offer a quote right now</h2>
        <p className="description">
          Unfortunately we&apos;re unable to provide an insurance quote for you at this moment.
          Please check back later.
        </p>
      </div>
      <div className="content-secondary">
        <Button
          type="primary"
          className="primary-cta"
          onClick={() => {
            form.resetFields();
            setStep('intro');
          }}
        >
          <span className="btn-text">Back to Insurance</span>
        </Button>
      </div>
    </div>
  );

  const renderLoading = () => (
    <div className="insurance-loading">
      <Spin size="large" />
    </div>
  );

  const startNewQuote = () => {
    form.resetFields();
    setStep('quote-info');
  };

  const renderPortal = () => {
    const messaging = application?.suggested_messaging;
    return (
      <div className="step-box portal-box">
        <div className="portal-header">
          <h2 className="step-title">Your insurance</h2>
          <button type="button" className="portal-link" onClick={startNewQuote}>
            Get a new quote
          </button>
        </div>

        {application && (
          <div className="portal-card">
            <div className="portal-card-head">
              <span className="portal-card-title">Application</span>
              <span className="status-pill">{applicationStatusLabel(application.status)}</span>
            </div>
            {messaging?.title && <p className="portal-msg-title">{messaging.title}</p>}
            {messaging?.subtitle && <p className="portal-msg-subtitle">{messaging.subtitle}</p>}
            {/*
              The backend's suggested call_to_action ("Continue") means "finish
              your application", but we can't resume it from here:
              retrieve_application_status returns no application URL, and
              retrieve_quoter returns no quote body, so start_application can't
              be re-issued after a reload. Send the user to Ladder to finish
              there rather than silently restarting the quote wizard.
            */}
            <Button
              type="primary"
              className="primary-cta"
              href={LADDER_LOGIN_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="btn-text">Continue on Ladder</span>
              <ArrowRightOutlined className="arrow-icon" />
            </Button>
          </div>
        )}

        {policies.length > 0 && (
          <div className="portal-card">
            <span className="portal-card-title">Active policies</span>
            <div className="policy-cards">
              {policies.map((p) => (
                <div className="policy-card" key={p.policy_id}>
                  <span className="policy-coverage">{formatCurrency(p.active_coverage)}</span>
                  <span className="policy-meta">
                    {p.active_term}-year term · {formatCurrency(p.active_monthly_premium)}/mo
                  </span>
                  <span className="policy-id">Policy {p.policy_id}</span>
                </div>
              ))}
            </div>
            <a
              className="portal-link"
              href={LADDER_LOGIN_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Review policies on Ladder →
            </a>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className={containerClass}
      style={{ ['--brand' as string]: token.colorPrimary } as React.CSSProperties}
    >
      <div className="insurance-wrapper">
        {step === 'loading' && renderLoading()}
        {step === 'intro' && renderIntro()}
        {step === 'quote-info' && renderQuoteInfo()}
        {step === 'results' && renderResults()}
        {step === 'no-offer' && renderNoOffer()}
        {step === 'portal' && renderPortal()}
      </div>
    </div>
  );
};

// Simple step header with a back control.
const StepHeader: React.FC<{ label: string; onBack: () => void }> = ({ label, onBack }) => (
  <div className="step-header">
    <button type="button" className="step-back" onClick={onBack}>
      ← Back
    </button>
    <span className="step-label">{label}</span>
  </div>
);

// Inline shield icon so we don't depend on a new asset; inherits currentColor.
const SafetyIcon: React.FC<{ muted?: boolean }> = ({ muted }) => (
  <svg
    className={`insurance-icon${muted ? ' muted' : ''}`}
    width="60"
    height="60"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 2 4 5v6c0 5 3.4 8.4 8 11 4.6-2.6 8-6 8-11V5l-8-3Z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

export default Insurance;
